/**
 * Memory display sections — single source of truth for how storage categories
 * surface in the Account UI.
 *
 * Two ideas live in this file:
 *
 *   1. SECTIONS — the on-screen grouping ("Min familie", "Om deg", "Husstand",
 *      "Bosted", "Søknadsreise"). Each section pulls from one or more storage
 *      categories. Many-to-one is allowed; one-to-many is not.
 *
 *   2. CATEGORY_LABELS — the Norwegian label for each of the seven canonical
 *      categories from coord-backend/src/models/memoryCategories.js. This is
 *      what shows in filter dropdowns and per-memory tags on Alle minner.
 *
 * Adding a new storage category later is a one-line add in both maps. Do not
 * branch on category names anywhere else in the UI — call into this util.
 *
 * Note: the canonical category list lives server-side in
 * coord-backend/src/models/memoryCategories.js (`getValidCategories()`).
 * Any new category added there must be reflected here in the same PR.
 */

export const SECTIONS = [
  {
    id: 'family',
    title: 'Min familie',
    categories: ['family_members']
  },
  {
    id: 'about_you',
    title: 'Om deg',
    categories: ['user_self', 'user_preferences']
  },
  {
    id: 'household',
    title: 'Husstand',
    categories: ['family_context']
  },
  {
    id: 'location',
    title: 'Bosted',
    categories: ['location_info']
  },
  {
    id: 'journey',
    title: 'Søknadsreise',
    categories: ['benefits_journey', 'key_dates']
  }
];

const CATEGORY_LABELS = {
  family_members: 'Familie',
  user_self: 'Om deg',
  user_preferences: 'Preferanser',
  family_context: 'Husstand',
  location_info: 'Bosted',
  benefits_journey: 'Støttereise',
  key_dates: 'Viktige datoer'
};

const SUBCATEGORY_LABELS = {
  // family_members subcategories — surface as person-type when present
  family_members: {
    children: 'Barn',
    adults: 'Voksne',
    parents: 'Foreldre',
    spouse: 'Ektefelle/partner'
  }
};

/**
 * Build category → section lookup once at module load.
 */
const CATEGORY_TO_SECTION_ID = SECTIONS.reduce((acc, section) => {
  section.categories.forEach(cat => {
    acc[cat] = section.id;
  });
  return acc;
}, {});

/**
 * Returns the display section that owns a storage category, or null if the
 * category is unknown (treat as "do not group" — the Alle minner card will
 * still surface it via search/filter).
 */
export function getSectionForCategory(category) {
  const id = CATEGORY_TO_SECTION_ID[category];
  if (!id) return null;
  return SECTIONS.find(s => s.id === id) || null;
}

/**
 * Norwegian label for a category, with optional subcategory refinement for
 * categories where the subcategory is more informative than the category
 * (today: family_members → "Barn"/"Voksne"/…).
 *
 * Falls back to 'Annet' for unknown categories so the UI never renders
 * "undefined" — the Alle minner filter still allows the user to find these.
 */
export function getCategoryLabel(category, subcategory) {
  if (subcategory && SUBCATEGORY_LABELS[category]?.[subcategory]) {
    return SUBCATEGORY_LABELS[category][subcategory];
  }
  return CATEGORY_LABELS[category] || 'Annet';
}

/**
 * Ordered list of (category, label) pairs for filter dropdowns. The order
 * matches SECTIONS so the dropdown reads top-to-bottom in the same order the
 * sections would appear if they were rendered as cards. Within a section
 * with multiple categories (e.g. journey: benefits_journey + key_dates),
 * category order matches the SECTIONS array.
 */
export function getCategoryFilterOptions() {
  return SECTIONS.flatMap(section =>
    section.categories.map(category => ({
      category,
      label: CATEGORY_LABELS[category] || category
    }))
  );
}
